import React, { useState, useEffect } from 'react';
import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Flag, AlertTriangle, CheckCircle, XCircle, Loader2, User, MessageSquare, FileText, ShieldOff, ShieldCheck} from 'lucide-react';import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';import AdminLayout from './AdminLayout';

const STATUS_COLORS = {
  pending: { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
  reviewed: { bg: '#dcfce7', text: '#166534', label: 'Reviewed' },
  dismissed: { bg: '#f3f4f6', text: '#6b7280', label: 'Dismissed' },
};

const TYPE_ICONS = {
  user: User,
  post: FileText,
  comment: MessageSquare,
};

export default function AdminCommunityReportsPage() {
  const { colors } = useAdminTheme();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ pending: 0, reviewed: 0, dismissed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resolving, setResolving] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  const [banning, setBanning] = useState(null);
  const [activeTab, setActiveTab] = useState('reports'); // reports | bans
  const [bans, setBans] = useState([]);
  const [bansLoading, setBansLoading] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/community-reports?status=${statusFilter}`, { withCredentials: true }),
        axios.get(`${API}/admin/community-reports/stats`, { withCredentials: true }),
      ]);
      setReports(reportsRes.data.reports || []);
      setStats(statsRes.data);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  const fetchBans = async () => {
    setBansLoading(true);
    try {
      const res = await axios.get(`${API}/admin/community-bans`, { withCredentials: true });
      setBans(res.data.bans || []);
    } catch { /* ignore */ }
    setBansLoading(false);
  };

  useEffect(() => { fetchReports(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === 'bans') fetchBans(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async (reportId, status) => {
    setResolving(reportId);
    try {
      await axios.put(`${API}/admin/community-reports/${reportId}`, {
        status,
        admin_notes: adminNotes[reportId] || '',
      }, { withCredentials: true });
      await fetchReports();
    } catch {
      /* ignore */
    }
    setResolving(null);
  };

  const handleBan = async (report) => {
    const targetUserId = report.target_type === 'user'
      ? report.target_id
      : report.target_context?.user_id;
    if (!targetUserId) return;

    setBanning(report.report_id);
    try {
      await axios.post(`${API}/admin/community-bans`, {
        user_id: targetUserId,
        reason: report.reason,
        report_id: report.report_id,
      }, { withCredentials: true });
      await fetchReports();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert(typeof d === 'string' ? d : 'Failed to ban');
    }
    setBanning(null);
  };

  const handleUnban = async (userId) => {
    try {
      await axios.delete(`${API}/admin/community-bans/${userId}`, { withCredentials: true });
      await fetchBans();
    } catch { /* ignore */ }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-community-reports">
        <div className="flex items-center gap-3 mb-6">
          <Flag className="h-6 w-6" style={{ color: colors.accent }} />
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>Community Reports</h1>
        </div>

        {/* Tabs: Reports | Bans */}
        <div className="flex gap-2 mb-5">
          {[
            { key: 'reports', label: 'Reports', icon: Flag },
            { key: 'bans', label: 'Banned Users', icon: ShieldOff },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all"
              style={{
                background: activeTab === key ? colors.accent : colors.card,
                color: activeTab === key ? '#fff' : colors.textSecondary,
                border: `1px solid ${colors.border}`,
              }}
              data-testid={`tab-${key}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'reports' ? (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Pending', value: stats.pending, color: '#f59e0b', icon: AlertTriangle },
            { label: 'Reviewed', value: stats.reviewed, color: '#22c55e', icon: CheckCircle },
            { label: 'Dismissed', value: stats.dismissed, color: '#6b7280', icon: XCircle },
            { label: 'Total', value: stats.total, color: colors.accent, icon: Flag },
          ].map(({ label, value, color, icon: Icon }) => (
            <div
              key={label}
              className="rounded-xl p-4 text-center"
              style={{ background: colors.card, border: `1px solid ${colors.border}` }}
            >
              <Icon className="h-5 w-5 mx-auto mb-1" style={{ color }} />
              <p className="text-2xl font-bold" style={{ color: colors.text }}>{value}</p>
              <p className="text-xs" style={{ color: colors.textSecondary }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-4">
          {['pending', 'reviewed', 'dismissed', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                statusFilter === s ? 'text-white' : ''
              }`}
              style={{
                background: statusFilter === s ? colors.accent : colors.card,
                color: statusFilter === s ? '#fff' : colors.textSecondary,
                border: `1px solid ${colors.border}`,
              }}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 rounded-xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
            <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: colors.textSecondary, opacity: 0.4 }} />
            <p className="text-sm font-medium" style={{ color: colors.text }}>No {statusFilter} reports</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => {
              const TypeIcon = TYPE_ICONS[r.target_type] || Flag;
              const statusInfo = STATUS_COLORS[r.status] || STATUS_COLORS.pending;

              return (
                <div
                  key={r.report_id}
                  className="rounded-xl p-4"
                  style={{ background: colors.card, border: `1px solid ${colors.border}` }}
                  data-testid={`report-${r.report_id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TypeIcon className="h-4 w-4" style={{ color: colors.accent }} />
                      <span className="text-sm font-semibold capitalize" style={{ color: colors.text }}>
                        {r.target_type} Report
                      </span>
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: statusInfo.bg, color: statusInfo.text }}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <span className="text-[10px]" style={{ color: colors.textSecondary }}>
                      {new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  {/* Reporter */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs" style={{ color: colors.textSecondary }}>Reported by:</span>
                    <span className="text-xs font-medium" style={{ color: colors.text }}>
                      {r.reporter?.name || 'Unknown'} {r.reporter?.username ? `(@${r.reporter.username})` : ''}
                    </span>
                  </div>

                  {/* Reason */}
                  <div className="rounded-lg p-2 mb-2" style={{ background: colors.background }}>
                    <p className="text-xs font-medium" style={{ color: colors.textSecondary }}>Reason</p>
                    <p className="text-sm" style={{ color: colors.text }}>{r.reason}</p>
                    {r.details && <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>{r.details}</p>}
                  </div>

                  {/* Target context */}
                  {r.target_context && (
                    <div className="rounded-lg p-2 mb-3" style={{ background: colors.background }}>
                      <p className="text-xs font-medium mb-1" style={{ color: colors.textSecondary }}>
                        Reported {r.target_type} content
                      </p>
                      {r.target_type === 'user' && (
                        <p className="text-xs" style={{ color: colors.text }}>
                          {r.target_context.name} ({r.target_context.email || r.target_context.username || r.target_context.user_id})
                        </p>
                      )}
                      {(r.target_type === 'post' || r.target_type === 'comment') && (
                        <p className="text-xs" style={{ color: colors.text }}>
                          {(r.target_context.content || '').slice(0, 200)}{(r.target_context.content || '').length > 200 ? '...' : ''}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Admin actions */}
                  {r.status === 'pending' && (
                    <div className="space-y-2 pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <input
                        type="text"
                        placeholder="Admin notes (optional)"
                        value={adminNotes[r.report_id] || ''}
                        onChange={(e) => setAdminNotes((prev) => ({ ...prev, [r.report_id]: e.target.value }))}
                        className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                        style={{ background: colors.background, border: `1px solid ${colors.border}`, color: colors.text }}
                        data-testid={`admin-notes-${r.report_id}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleBan(r)}
                          disabled={banning === r.report_id || resolving === r.report_id}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white transition-all"
                          style={{ background: '#ef4444' }}
                          data-testid={`ban-${r.report_id}`}
                        >
                          {banning === r.report_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                          Ban User
                        </button>
                        <button
                          onClick={() => handleResolve(r.report_id, 'reviewed')}
                          disabled={resolving === r.report_id}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white transition-all"
                          style={{ background: '#22c55e' }}
                          data-testid={`resolve-${r.report_id}`}
                        >
                          {resolving === r.report_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                          Mark Reviewed
                        </button>
                        <button
                          onClick={() => handleResolve(r.report_id, 'dismissed')}
                          disabled={resolving === r.report_id}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                          style={{ background: colors.background, color: colors.textSecondary, border: `1px solid ${colors.border}` }}
                          data-testid={`dismiss-${r.report_id}`}
                        >
                          <XCircle className="h-3 w-3" />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolved info */}
                  {r.status !== 'pending' && r.admin_notes && (
                    <div className="text-xs mt-2 pt-2" style={{ borderTop: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                      Admin notes: {r.admin_notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        ) : (
        /* Banned Users Tab */
        <div data-testid="banned-users-tab">
          {bansLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
            </div>
          ) : bans.length === 0 ? (
            <div className="text-center py-12 rounded-xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
              <ShieldCheck className="h-12 w-12 mx-auto mb-3" style={{ color: colors.textSecondary, opacity: 0.4 }} />
              <p className="text-sm font-medium" style={{ color: colors.text }}>No banned users</p>
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>Community is clean!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bans.map((b) => (
                <div
                  key={b.ban_id}
                  className="rounded-xl p-4 flex items-center justify-between"
                  style={{ background: colors.card, border: `1px solid ${colors.border}` }}
                  data-testid={`ban-${b.ban_id}`}
                >
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>
                      {b.user_info?.name || 'Unknown'} {b.user_info?.username ? `(@${b.user_info.username})` : ''}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>{b.user_info?.email}</p>
                    <p className="text-xs mt-1" style={{ color: '#ef4444' }}>Reason: {b.reason}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: colors.textSecondary }}>
                      Banned on {new Date(b.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleUnban(b.user_id)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{ background: '#22c55e', color: '#fff' }}
                    data-testid={`unban-${b.user_id}`}
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Unban
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </AdminLayout>
  );
}
